'use client';

import { useRef, useEffect, useCallback } from 'react';
import {
  PayPalScriptProvider,
  PayPalButtons,
  usePayPalScriptReducer,
} from '@paypal/react-paypal-js';
import { Button, primaryOutlinedButtonSx } from '@elevator/ui';
import { PayPalButtonsComponentOptions } from '@paypal/paypal-js/types/components/buttons';

/**
 * Default style configuration for PayPal buttons.
 * Provides consistent styling across all PayPal button instances.
 */
const DEFAULT_PAYPAL_STYLE: PayPalButtonsComponentOptions['style'] = {
  label: 'pay',
  layout: 'horizontal',
  color: 'black',
  tagline: false,
  height: 40,
};

/**
 * PayPalButtonInner Component
 *
 * Inner component that uses PayPal hooks and handles intent changes dynamically.
 * This component must be rendered within a PayPalScriptProvider context.
 *
 * Features:
 * - Handles dynamic intent changes (authorize/capture) using PayPal's resetOptions
 * - Manages loading state via PayPal script reducer
 * - Provides fallback disabled button when disabled prop is true
 * - Handles errors and forwards them to parent component
 *
 * @param props - Component props
 * @param props.intent - Payment intent ('authorize' | 'capture')
 * @param props.disabled - Whether the button should be disabled
 * @param props.style - Custom style overrides for PayPal buttons
 * @param props.onError - Error callback function
 * @param props.clientId - PayPal client ID for script configuration
 * @param props.rest - All other PayPalButtonsComponentOptions props
 */
const PayPalButtonInner = (props: PayPalButtonInnerProps) => {
  const { intent, disabled, style, onError, clientId, ...rest } = props;
  const [{ isPending }, dispatch] = usePayPalScriptReducer();
  const previousIntent = useRef(intent);

  /**
   * Handles PayPal script errors.
   * Forwards errors to the parent component's onError callback for handling.
   */
  const handleError = useCallback(
    (err: unknown) => {
      onError?.(err);
    },
    [onError],
  );

  /**
   * Handles intent changes by resetting PayPal script options.
   * This is the recommended approach per PayPal documentation for dynamic intent changes.
   */
  useEffect(() => {
    if (previousIntent.current !== intent) {
      // Note: PayPal SDK's resetOptions action type is not fully exported in types
      // Using type assertion as workaround until SDK types are updated
      dispatch({
        type: 'resetOptions' as any,
        value: {
          clientId,
          intent,
          components: 'buttons',
          disableFunding: 'credit,card',
        },
      });
      previousIntent.current = intent;
    }
  }, [intent, dispatch, clientId]);

  if (disabled) {
    return (
      <Button
        variant={'outlined'}
        sx={primaryOutlinedButtonSx}
        disabled
        fullWidth
        aria-label="PayPal payment (disabled)"
        data-component="PayPalButton"
      >
        PayPal
      </Button>
    );
  }

  return (
    <div data-component="PayPalButton">
      <PayPalButtons
        style={{
          ...DEFAULT_PAYPAL_STYLE,
          ...style,
        }}
        disabled={isPending || disabled}
        onError={handleError}
        {...rest}
      />
    </div>
  );
};

interface PayPalButtonInnerProps extends Omit<PayPalButtonProps, 'intent'> {
  /** Payment intent ('authorize' | 'capture') */
  intent: 'authorize' | 'capture';
  /** Custom style overrides for PayPal buttons */
  style?: PayPalButtonsComponentOptions['style'];
  /** Error callback function */
  onError?: (err: unknown) => void;
  /** PayPal client ID for script configuration */
  clientId: string;
}

/**
 * PayPalButton Component
 *
 * Main PayPal payment button component with provider wrapper.
 * Integrates PayPal's payment SDK to provide a seamless payment experience.
 *
 * Features:
 * - Wraps PayPal buttons in PayPalScriptProvider for proper SDK initialization
 * - Handles missing client ID configuration gracefully
 * - Supports both authorize and capture payment intents
 * - Provides fallback UI when PayPal client ID is not configured
 * - Disables credit card and credit funding options (PayPal only)
 *
 * @param props - Component props
 * @param props.intent - Payment intent ('authorize' | 'capture')
 * @param props.disabled - Whether the button should be disabled
 * @param props.createOrder - Callback to create PayPal order
 * @param props.onApprove - Callback when payment is approved
 * @param props.onError - Error callback function
 * @param props.style - Custom style overrides for PayPal buttons
 * @param props.rest - All other PayPalButtonsComponentOptions props
 */
const PayPalButton = (props: PayPalButtonProps) => {
  const { intent, disabled, ...rest } = props;

  const clientId = process.env.PAYPAL_CLIENT_ID;

  if (!clientId) {
    return (
      <Button
        variant={'outlined'}
        sx={primaryOutlinedButtonSx}
        disabled
        fullWidth
        aria-label="PayPal payment (configuration error)"
        data-component="PayPalButton"
      >
        PayPal (Configuration Error)
      </Button>
    );
  }

  return (
    <div data-component="PayPalButton">
      <PayPalScriptProvider
        options={{
          clientId,
          intent,
          components: 'buttons',
          disableFunding: 'credit,card',
        }}
      >
        <PayPalButtonInner
          intent={intent}
          disabled={disabled}
          clientId={clientId}
          {...rest}
        />
      </PayPalScriptProvider>
    </div>
  );
};

export type PayPalButtonProps = Omit<PayPalButtonsComponentOptions, 'fundingSource'> & {
  disabled?: boolean;
  intent: 'authorize' | 'capture';
};

export default PayPalButton;
